import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { WidgetCard } from './widget-card';
import { ClientPreviewWidgetData, WidgetProps } from './widget-types';

/**
 * A tela que o cliente dele vai ver.
 *
 * É o cartão do fecho: o prestador confere o negócio pelos olhos de quem vai
 * contratar, não por uma lista de campos preenchidos — e **é aqui que ele diz
 * que está certo**. Passar direto para o resumo dava a impressão de que a
 * conversa tinha acabado sozinha; "Quero ajustar" abre o menu de partes.
 */
export function ClientPreviewWidget({
  data,
  editable,
  onAdjust,
}: WidgetProps<ClientPreviewWidgetData>) {
  return (
    <WidgetCard title={data.activityLabel ?? 'Como seu cliente vê'} icon="eye-outline">
      <View className="gap-2">
        {(data.services ?? []).map((service) => (
          <View
            key={service.name}
            className="flex-row items-center gap-3 rounded-xl bg-neutral-50 px-3 py-2.5"
          >
            <View className="flex-1 gap-0.5">
              <ThemedText className="font-medium text-sm text-foreground">
                {service.name}
              </ThemedText>
              <ThemedText className="text-foreground/50 text-xs">
                {service.recurrenceLabel
                  ? `${service.durationLabel} · ${service.recurrenceLabel}`
                  : service.durationLabel}
              </ThemedText>
            </View>
            <ThemedText className="font-semibold text-sm text-foreground">
              {service.priceLabel}
            </ThemedText>
          </View>
        ))}
      </View>

      {(data.scheduleLines ?? []).length > 0 ? (
        <View className="gap-1">
          {data.scheduleLines.map((line, index) => (
            <View key={index} className="flex-row items-center gap-2">
              <Ionicons className="text-neutral-400" name="time-outline" size={13} />
              <ThemedText className="text-foreground/60 text-xs">{line}</ThemedText>
            </View>
          ))}
        </View>
      ) : null}

      {data.locationLine ? (
        <View className="flex-row items-center gap-2">
          <Ionicons className="text-neutral-400" name="location-outline" size={13} />
          <ThemedText className="text-foreground/60 text-xs">{data.locationLine}</ThemedText>
        </View>
      ) : null}

      {data.teamLine ? (
        <View className="flex-row items-center gap-2">
          <Ionicons className="text-neutral-400" name="people-outline" size={13} />
          <ThemedText className="text-foreground/60 text-xs">{data.teamLine}</ThemedText>
        </View>
      ) : null}

      {data.paymentLine ? (
        <View className="flex-row items-center gap-2">
          <Ionicons className="text-neutral-400" name="wallet-outline" size={13} />
          <ThemedText className="text-foreground/60 text-xs">{data.paymentLine}</ThemedText>
        </View>
      ) : null}

      {editable && onAdjust && data.confirmLabel ? (
        <View className="gap-2">
          <PressableScale
            onPress={() => onAdjust({ done: true }, data.confirmLabel)}
            className="items-center rounded-full bg-primary py-2.5"
          >
            <ThemedText className="font-semibold text-sm text-white">
              {data.confirmLabel}
            </ThemedText>
          </PressableScale>
          {data.adjustLabel ? (
            <PressableScale
              onPress={() => onAdjust({}, data.adjustLabel)}
              className="items-center py-1"
            >
              <ThemedText className="text-foreground/45 font-semibold text-xs">
                {data.adjustLabel}
              </ThemedText>
            </PressableScale>
          ) : null}
        </View>
      ) : null}
    </WidgetCard>
  );
}
